import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const SplitTargetModal = ({ show, organization, onClose, onSuccess }) => {
    const [children, setChildren] = useState([]);
    const [splits, setSplits] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalAllocated, setTotalAllocated] = useState(0);
    
    // Add month/year selection
    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        if (show && organization) {
            fetchChildren();
        }
    }, [show, organization, selectedMonth, selectedYear]);

    useEffect(() => {
        const total = Object.values(splits).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
        setTotalAllocated(total);
    }, [splits]);

    const fetchChildren = async () => {
        try {
            const response = await axiosInstance.get('/api/sales-target/organizations/tree');
            if (response.data.success) {
                const findChildren = (nodes) => {
                    for (const node of nodes) {
                        if (node._id === organization._id) {
                            return node.children || [];
                        }
                        if (node.children) {
                            const found = findChildren(node.children);
                            if (found.length > 0) return found;
                        }
                    }
                    return [];
                };
                
                const childNodes = findChildren(response.data.data);
                setChildren(childNodes);
                
                // Initialize splits with current monthly targets
                const initialSplits = {};
                for (const child of childNodes) {
                    try {
                        // Get the monthly target for this child
                        const targetResponse = await axiosInstance.get('/api/sales-target/organization-targets', {
                            params: {
                                organizationId: child._id,
                                month: selectedMonth,
                                year: selectedYear
                            }
                        });
                        initialSplits[child._id] = targetResponse.data.success ? targetResponse.data.data.target : 0;
                    } catch (err) {
                        // Fallback to organization.target if monthly target not found
                        initialSplits[child._id] = child.target || 0;
                    }
                }
                setSplits(initialSplits);
            }
        } catch (err) {
            console.error('Error fetching children:', err);
            setError('Failed to load child organizations');
        }
    };

    // Get parent target for the selected month/year
    const getParentTarget = async () => {
        try {
            const response = await axiosInstance.get('/api/sales-target/organization-targets', {
                params: {
                    organizationId: organization._id,
                    month: selectedMonth,
                    year: selectedYear
                }
            });
            return response.data.success ? response.data.data.target : organization.target || 0;
        } catch (err) {
            return organization.target || 0;
        }
    };

    const [parentTarget, setParentTarget] = useState(0);

    useEffect(() => {
        if (show && organization) {
            getParentTarget().then(setParentTarget);
        }
    }, [show, organization, selectedMonth, selectedYear]);

    const handleSplitChange = (childId, value) => {
        setSplits(prev => ({
            ...prev,
            [childId]: value
        }));
    };

    const distributeEvenly = () => {
        if (children.length === 0 || parentTarget === 0) return;
        const perChild = (parentTarget / children.length).toFixed(2);
        const newSplits = {};
        children.forEach(child => {
            newSplits[child._id] = perChild;
        });
        setSplits(newSplits);
    };

    const distributeProportionally = () => {
        if (children.length === 0 || parentTarget === 0) return;
        const currentTotal = Object.values(splits).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
        if (currentTotal === 0) {
            distributeEvenly();
            return;
        }
        
        const newSplits = {};
        children.forEach(child => {
            const currentValue = parseFloat(splits[child._id]) || 0;
            const proportion = currentValue / currentTotal;
            newSplits[child._id] = (parentTarget * proportion).toFixed(2);
        });
        setSplits(newSplits);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (Math.abs(totalAllocated - parentTarget) > 0.01) {
            setError(`Total allocated (${totalAllocated.toFixed(2)}) must equal parent target (${parentTarget})`);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Update each child's target using the new API
            const updatePromises = children.map(child => 
                axiosInstance.post('/api/sales-target/organization-targets', {
                    organizationId: child._id,
                    month: selectedMonth,
                    year: selectedYear,
                    target: parseFloat(splits[child._id])
                })
            );

            await Promise.all(updatePromises);
            onSuccess();
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to split targets');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSplits({});
        setError(null);
        onClose();
    };

    if (!show || !organization) return null;

    const remaining = parentTarget - totalAllocated;
    const isValid = Math.abs(remaining) < 0.01;

    return (
        <div className="modal-overlay split-target-modal" onClick={handleClose}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <h2>Split Target: {organization.name}</h2>
                
                {/* Month/Year Selection */}
                <div className="period-selection">
                    <div className="form-group">
                        <label>Month:</label>
                        <select 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        >
                            {months.map((month, index) => (
                                <option key={index + 1} value={index + 1}>
                                    {month}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Year:</label>
                        <select 
                            value={selectedYear} 
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        >
                            {Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() + i - 2).map(year => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                
                <div className="split-summary">
                    <div className="summary-item">
                        <span className="label">Parent Target ({months[selectedMonth - 1]} {selectedYear}):</span>
                        <span className="value">{parentTarget.toLocaleString()}</span>
                    </div>
                    <div className="summary-item">
                        <span className="label">Total Allocated:</span>
                        <span className={`value ${isValid ? 'valid' : 'invalid'}`}>
                            {totalAllocated.toLocaleString()}
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="label">Remaining:</span>
                        <span className={`value ${remaining === 0 ? 'valid' : 'invalid'}`}>
                            {remaining.toFixed(2)}
                        </span>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                {children.length === 0 ? (
                    <div className="empty-state">
                        <p>No child organizations found. Create child organizations first.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="split-actions">
                            <button type="button" onClick={distributeEvenly} className="btn-secondary">
                                Distribute Evenly
                            </button>
                            <button type="button" onClick={distributeProportionally} className="btn-secondary">
                                Distribute Proportionally
                            </button>
                        </div>

                        <div className="split-list">
                            {children.map(child => (
                                <div key={child._id} className="split-item">
                                    <div className="split-info">
                                        <span className={`type-badge ${child.type}`}>
                                            {child.type === 'company' && '🏭'}
                                            {child.type === 'branch' && '🏪'}
                                        </span>
                                        <span className="child-name">{child.name}</span>
                                        <span className="current-target">
                                            Current: {(splits[child._id] || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="split-input">
                                        <input
                                            type="number"
                                            value={splits[child._id] || ''}
                                            onChange={(e) => handleSplitChange(child._id, e.target.value)}
                                            placeholder="Enter target"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="modal-actions">
                            <button type="button" onClick={handleClose} className="btn-secondary" disabled={loading}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primary" disabled={loading || !isValid}>
                                {loading ? 'Applying...' : 'Apply Split'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SplitTargetModal;
